"use client";

import { useState, useRef } from "react";
import { X, FileText, ImageIcon, Video } from "lucide-react";
import StepMarker from "./StepMarker";
import { Button } from "./Button";

export default function Step4MediaUpload({
  formData,
  updateFormData,
  onBack,
  onNext,
}) {
  const [certificateFile, setCertificateFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);

  const certificateInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const handleCertificateChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setCertificateFile(file);
      updateFormData({ businesscertificate: file });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      updateFormData({ businessimageurl: file });
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setVideoFile(file);
      updateFormData({ businessvideourl: file });
    }
  };

  const removeCertificate = () => {
    setCertificateFile(null);
    updateFormData({ businesscertificate: null });
    if (certificateInputRef.current) {
      certificateInputRef.current.value = "";
    }
  };

  const removeImage = () => {
    setImageFile(null);
    updateFormData({ businessimageurl: null });
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const removeVideo = () => {
    setVideoFile(null);
    updateFormData({ businessvideourl: null });
    if (videoInputRef.current) {
      videoInputRef.current.value = "";
    }
  };

  return (
    <div>
      <StepMarker currentStep={4} totalSteps={5} onBack={onBack} />

      <h2 className="text-2xl font-bold mb-1">Business Media</h2>
      <p className="text-gray-500 mb-6">
        Upload your business documents and media
      </p>

      <div className="space-y-6">
        {/* Business Certificate */}
        <div>
          <div className="uppercase text-xs font-medium text-gray-500 mb-2">
            BUSINESS CERTIFICATE (OPTIONAL)
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
            <input
              type="file"
              ref={certificateInputRef}
              onChange={handleCertificateChange}
              className="hidden"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />

            {certificateFile ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FileText size={24} className="text-blue-500 mr-3" />
                  <div>
                    <div className="font-medium">{certificateFile.name}</div>
                    <div className="text-sm text-gray-500">
                      {(certificateFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </div>
                <button
                  onClick={removeCertificate}
                  className="text-red-500 hover:text-red-700"
                >
                  <X size={20} />
                </button>
              </div>
            ) : (
              <div className="text-center">
                <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                <button
                  onClick={() => certificateInputRef.current?.click()}
                  className="text-[#7B189F] hover:text-[#671485] font-medium"
                >
                  Upload Business Certificate
                </button>
                <p className="text-sm text-gray-500 mt-2">
                  PDF, DOC, DOCX, JPG, PNG up to 10MB
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Business Image */}
        <div>
          <div className="uppercase text-xs font-medium text-gray-500 mb-2">
            BUSINESS IMAGE (OPTIONAL)
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
            <input
              type="file"
              ref={imageInputRef}
              onChange={handleImageChange}
              className="hidden"
              accept="image/*"
            />

            {imageFile ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg mr-3 overflow-hidden">
                    <img
                      src={URL.createObjectURL(imageFile) || "/placeholder.svg"}
                      alt="Business"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <div className="font-medium">{imageFile.name}</div>
                    <div className="text-sm text-gray-500">
                      {(imageFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </div>
                <button
                  onClick={removeImage}
                  className="text-red-500 hover:text-red-700"
                >
                  <X size={20} />
                </button>
              </div>
            ) : (
              <div className="text-center">
                <ImageIcon size={48} className="mx-auto text-gray-400 mb-4" />
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className="text-[#7B189F] hover:text-[#671485] font-medium"
                >
                  Upload Business Image
                </button>
                <p className="text-sm text-gray-500 mt-2">
                  JPG, PNG, GIF up to 5MB
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Business Video */}
        <div>
          <div className="uppercase text-xs font-medium text-gray-500 mb-2">
            BUSINESS VIDEO (OPTIONAL)
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
            <input
              type="file"
              ref={videoInputRef}
              onChange={handleVideoChange}
              className="hidden"
              accept="video/*"
            />

            {videoFile ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Video size={24} className="text-purple-500 mr-3" />
                  <div>
                    <div className="font-medium">{videoFile.name}</div>
                    <div className="text-sm text-gray-500">
                      {(videoFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </div>
                <button
                  onClick={removeVideo}
                  className="text-red-500 hover:text-red-700"
                >
                  <X size={20} />
                </button>
              </div>
            ) : (
              <div className="text-center">
                <Video size={48} className="mx-auto text-gray-400 mb-4" />
                <button
                  onClick={() => videoInputRef.current?.click()}
                  className="text-[#7B189F] hover:text-[#671485] font-medium"
                >
                  Upload Business Video
                </button>
                <p className="text-sm text-gray-500 mt-2">
                  MP4, MOV, AVI up to 50MB
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onNext} className="px-8">
          Continue
        </Button>
      </div>
    </div>
  );
}
